import { forwardRef } from "react";
import {
  Close,
  Portal,
  Content as RadixContent,
  Root,
  Trigger,
} from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { DismissIcon } from "../../materials/icons/dismiss-icon/DismissIcon";
import { useScrollPosition } from "../../utils/hooks/useScrollPosition";
import { Heading } from "../heading/Heading";
import { IconButton } from "../icon-button/IconButton.Parts";
import {
  Container,
  Content,
  Header,
  Overlay,
  Props,
  Title,
} from "./Drawer.Parts";

export const Drawer = forwardRef<HTMLDivElement, Props>(
  (
    {
      defaultOpen,
      open,
      onOpenChange,
      trigger,
      origin = "right",
      title,
      hideTitle = false,
      includeDescription = false,
      children,
      ...props
    },
    ref,
  ) => {
    const [[scrolled], scrollProps] = useScrollPosition();

    return (
      <Root defaultOpen={defaultOpen} open={open} onOpenChange={onOpenChange}>
        {trigger && <Trigger asChild>{trigger}</Trigger>}
        <Portal>
          <Overlay ref={ref} {...props}>
            <RadixContent
              asChild
              {...(!includeDescription && { "aria-describedby": undefined })}
            >
              <Content $origin={origin}>
                <Header>
                  <Close asChild>
                    <IconButton>
                      <DismissIcon />
                    </IconButton>
                  </Close>
                </Header>
                {hideTitle ? (
                  <VisuallyHidden>
                    <Title>{title}</Title>
                  </VisuallyHidden>
                ) : (
                  <Title asChild>
                    <Heading variant="section">{title}</Heading>
                  </Title>
                )}
                <Container {...scrollProps} $topShadow={scrolled > 0}>
                  {children}
                </Container>
              </Content>
            </RadixContent>
          </Overlay>
        </Portal>
      </Root>
    );
  },
);
